/**
 * Executive Membership Builder
 * Clean rebuild with robust initialization and state management
 */

class MembershipBuilder {
  constructor() {
    // Load data from JSON block
    this.data = this.loadData();
    if (!this.data) {
      console.error('MembershipBuilder: Failed to load data');
      return;
    }

    // State - quantity null until user selects
    this.state = {
      plan: '6month',
      quantity: null,
      soaps: {},      // { variantId: { product, quantity } }
      addons: {},     // { variantId: { product, quantity } }
      currentFilter: 'all'
    };
    
    // Flag to prevent saveState during restoreState
    this._restoring = false;

    // Cache DOM elements
    this.cacheElements();
    
    // Initialize
    this.init();
  }

  /**
   * Load data from JSON script block
   */
  loadData() {
    const dataEl = document.getElementById('membership-data');
    if (!dataEl) {
      console.error('MembershipBuilder: #membership-data element not found');
      return null;
    }

    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('MembershipBuilder: Failed to parse data JSON', e);
      return null;
    }
  }

  /**
   * Cache frequently accessed DOM elements
   */
  cacheElements() {
    this.elements = {
      // Builder container
      builderContainer: document.querySelector('.membership-builder'),
      plansPlaceholder: document.querySelector('[data-plans-placeholder]'),
      
      // Plan & Quantity
      planCards: document.querySelectorAll('[data-plan]'),
      quantityCards: document.querySelectorAll('[data-quantity]'),
      
      // Steps
      steps: document.querySelectorAll('.membership-step'),
      
      // Products
      soapsGrid: document.querySelector('[data-products-grid="soaps"]'),
      addonsGrid: document.querySelector('[data-products-grid="addons"]'),
      routineSection: document.querySelector('[data-routine-section]'),
      routineGrid: document.querySelector('[data-routine-grid]'),
      browseAllBtn: document.querySelector('[data-browse-all-addons]'),
      fullAddonsGrid: document.querySelector('[data-full-addons]'),
      filters: document.querySelectorAll('[data-filter]'),
      
      // Counter displays
      barsNeeded: document.querySelectorAll('[data-bars-needed]'),
      selectedCount: document.querySelectorAll('[data-selected-count]'),
      
      // Summary
      summaryPlan: document.querySelector('[data-summary-plan]'),
      summaryQuantity: document.querySelector('[data-summary-quantity]'),
      summaryOilCredits: document.querySelector('[data-summary-oil-credits]'),
      summaryOilCreditsSection: document.querySelector('[data-summary-oil-credits-section]'),
      summaryShipping: document.querySelector('[data-summary-shipping]'),
      summarySoaps: document.querySelector('[data-summary-soaps]'),
      summaryAddons: document.querySelector('[data-summary-addons]'),
      summaryAddonsSection: document.querySelector('[data-summary-addons-section]'),
      totalAmount: document.querySelector('[data-total-amount]'),
      totalLabel: document.querySelector('[data-total-label]'),
      quarterlyAmount: document.querySelector('[data-quarterly-amount]'),
      checkoutBtn: document.querySelector('[data-checkout-btn]'),
      
      // Sticky bar
      stickyBar: document.querySelector('[data-sticky-bar]'),
      stickyCount: document.querySelector('[data-sticky-count]'),
      stickyTotal: document.querySelector('[data-sticky-total]'),
      stickyPrice: document.querySelector('[data-sticky-price]'),
      stickyCheckout: document.querySelector('[data-sticky-checkout]'),
      stickySummary: document.querySelector('[data-sticky-summary]'),
      stickySummaryToggle: document.querySelector('[data-sticky-summary-toggle]'),
      stickySummaryBody: document.querySelector('[data-sticky-summary-body]'),
      stickyChevron: document.querySelector('[data-sticky-chevron]'),
      stickyPlanName: document.querySelector('[data-sticky-plan-name]'),
      stickyQtyName: document.querySelector('[data-sticky-qty-name]'),
      stickySoapsList: document.querySelector('[data-sticky-soaps-list]'),
      stickyAddonsRow: document.querySelector('[data-sticky-addons-row]'),
      stickyAddonsList: document.querySelector('[data-sticky-addons-list]'),
      stickySummaryTotal: document.querySelector('[data-sticky-summary-total]'),
      
      // Price displays
      planPrices: {
        annual: document.querySelector('[data-plan-price="annual"]'),
        '6month': document.querySelector('[data-plan-price="6month"]'),
        quarterly: document.querySelector('[data-plan-price="quarterly"]')
      },
      planNotes: {
        annual: document.querySelector('[data-plan-note="annual"]'),
        '6month': document.querySelector('[data-plan-note="6month"]'),
        quarterly: document.querySelector('[data-plan-note="quarterly"]')
      }
    };
  }

  /**
   * Initialize the builder
   */
  init() {
    // Set initial no-quantity and no-plan state
    if (this.elements.builderContainer) {
      this.elements.builderContainer.classList.add('membership-builder--no-quantity');
      this.elements.builderContainer.classList.add('membership-builder--no-plan');
    }
    
    this.renderProducts();
    this.bindEvents();
    this.restoreState();
    this.updateUI();
    this.initStickyBar();
    
    console.log('MembershipBuilder initialized', this.data);
    console.log('Selling plans (dynamic):', this.data.sellingPlans);
    console.log('Selling plans (fallback):', this.data.sellingPlansFallback);
  }

  /**
   * Save current state to localStorage
   */
  saveState() {
    if (this._restoring) return;
    try {
      const saved = {
        plan: this.state.plan,
        quantity: this.state.quantity,
        soaps: {},
        addons: {}
      };
      
      // Store variant IDs and quantities only (products are re-matched on restore)
      Object.entries(this.state.soaps).forEach(([id, item]) => {
        saved.soaps[id] = item.quantity;
      });
      Object.entries(this.state.addons).forEach(([id, item]) => {
        saved.addons[id] = item.quantity;
      });
      
      localStorage.setItem('lumbazzi_membership', JSON.stringify(saved));
    } catch (e) {
      // localStorage unavailable or full — silently skip
    }
  }

  /**
   * Restore state from localStorage
   */
  restoreState() {
    try {
      const raw = localStorage.getItem('lumbazzi_membership');
      if (!raw) return;
      
      const saved = JSON.parse(raw);
      this._restoring = true;
      
      // Restore quantity first (triggers UI unlock)
      if (saved.quantity && [4, 6, 8].includes(saved.quantity)) {
        this.selectQuantity(saved.quantity);
      }
      
      // Restore plan
      if (saved.plan && this.data.plans[saved.plan]) {
        this.selectPlan(saved.plan);
      }
      
      // Restore soap selections
      if (saved.soaps) {
        const allSoaps = this.data.products.soaps || [];
        Object.entries(saved.soaps).forEach(([variantId, qty]) => {
          const product = allSoaps.find(p => p.variantId.toString() === variantId.toString());
          if (product && qty > 0) {
            this.state.soaps[variantId] = { product, quantity: Math.min(qty, this.state.quantity || 8) };
            this.updateProductCard(variantId, false);
          }
        });
        // Trim if total exceeds quantity
        this.trimSoapSelections();
      }
      
      // Restore addon selections
      if (saved.addons) {
        const allAddons = this.data.products.addons || [];
        Object.entries(saved.addons).forEach(([variantId, qty]) => {
          const product = allAddons.find(p => p.variantId.toString() === variantId.toString());
          if (product && qty > 0) {
            this.state.addons[variantId] = { product, quantity: qty };
            this.updateProductCard(variantId, true);
          }
        });
      }
      
      this._restoring = false;
      console.log('MembershipBuilder: State restored from localStorage');
    } catch (e) {
      this._restoring = false;
      // Corrupted data — clear and start fresh
      localStorage.removeItem('lumbazzi_membership');
    }
  }

  /**
   * Render product grids
   */
  renderProducts() {
    // Render soaps
    if (this.elements.soapsGrid && this.data.products.soaps) {
      if (this.data.products.soaps.length > 0) {
        this.elements.soapsGrid.innerHTML = this.data.products.soaps
          .map(product => this.renderProductCard(product, false))
          .join('');
      } else {
        this.elements.soapsGrid.innerHTML = '<p class="membership-products__empty">No subscription soaps available. Please configure the collection in theme settings.</p>';
      }
    }

    // Render addons
    if (this.elements.addonsGrid && this.data.products.addons) {
      if (this.data.products.addons.length > 0) {
        this.elements.addonsGrid.innerHTML = this.data.products.addons
          .map(product => this.renderProductCard(product, true))
          .join('');
      } else {
        this.elements.addonsGrid.innerHTML = '<p class="membership-products__empty">No add-on products available.</p>';
      }
    }
  }

  /**
   * Render a single product card
   */
  renderProductCard(product, isAddon = false) {
    const scent = this.data.scentProfiles?.[product.title] || '';
    const categoryAttr = isAddon && product.category ? `data-category="${product.category}"` : '';
    const cardClass = isAddon ? 'membership-product membership-product--addon' : 'membership-product';
    
    return `
      <div class="${cardClass}" 
           data-variant-id="${product.variantId}"
           data-product-id="${product.id}"
           data-price="${product.price}"
           data-title="${this.escapeHtml(product.title)}"
           data-is-addon="${isAddon}"
           ${categoryAttr}>
        <div class="membership-product__image">
          ${product.image 
            ? `<img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
            : '<div class="membership-product__placeholder">No Image</div>'
          }
        </div>
        <div class="membership-product__info">
          <h4 class="membership-product__title">${this.escapeHtml(product.title)}</h4>
          ${scent ? `<p class="membership-product__scent">${scent}</p>` : ''}
          ${isAddon ? `<p class="membership-product__price">${this.formatMoney(product.price)}</p>` : ''}
        </div>
        <div class="membership-product__actions">
          <button class="membership-product__add" type="button" data-action="add">
            + Add
          </button>
          <div class="membership-product__qty" style="display: none;">
            <button class="membership-product__qty-btn" type="button" data-action="decrease">−</button>
            <span class="membership-product__qty-value">0</span>
            <button class="membership-product__qty-btn" type="button" data-action="increase">+</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Plan selection
    this.elements.planCards.forEach(card => {
      card.addEventListener('click', () => this.selectPlan(card.dataset.plan));
    });

    // Inherited benefit dropdowns (mobile only — stop propagation so plan doesn't select)
    document.querySelectorAll('[data-inherited-toggle]').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        if (window.innerWidth <= 768) {
          e.stopPropagation();
          toggle.classList.toggle('expanded');
        }
      });
    });

    // Quantity selection
    this.elements.quantityCards.forEach(card => {
      card.addEventListener('click', () => this.selectQuantity(parseInt(card.dataset.quantity)));
    });

    // Product actions (delegated)
    if (this.elements.soapsGrid) {
      this.elements.soapsGrid.addEventListener('click', (e) => this.handleProductClick(e, false));
    }
    if (this.elements.addonsGrid) {
      this.elements.addonsGrid.addEventListener('click', (e) => this.handleProductClick(e, true));
    }

    // Routine curated cards (delegated)
    if (this.elements.routineGrid) {
      this.elements.routineGrid.addEventListener('click', (e) => this.handleRoutineCardClick(e));
    }

    // Browse all add-ons toggle
    if (this.elements.browseAllBtn) {
      this.elements.browseAllBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const fullGrid = this.elements.fullAddonsGrid;
        if (!fullGrid) return;
        const isVisible = fullGrid.style.display !== 'none';
        fullGrid.style.display = isVisible ? 'none' : 'block';
        this.elements.browseAllBtn.textContent = isVisible ? 'Browse all add-ons →' : '← Back to recommendations';
      });
    }

    // Filters
    this.elements.filters.forEach(filter => {
      filter.addEventListener('click', () => this.filterAddons(filter.dataset.filter));
    });

    // Checkout buttons
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.addEventListener('click', () => this.checkout());
    }
    if (this.elements.stickyCheckout) {
      this.elements.stickyCheckout.addEventListener('click', () => this.checkout());
    }

    // Window resize for sticky bar
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Handle product card clicks
   */
  handleProductClick(e, isAddon) {
    const action = e.target.dataset?.action;
    if (!action) return;

    const card = e.target.closest('.membership-product');
    if (!card) return;

    const variantId = card.dataset.variantId;
    const items = isAddon ? this.state.addons : this.state.soaps;

    if (action === 'add') {
      this.addProduct(variantId, isAddon);
    } else if (action === 'increase') {
      this.updateProductQuantity(variantId, 1, isAddon);
    } else if (action === 'decrease') {
      this.updateProductQuantity(variantId, -1, isAddon);
    }
  }

  /**
   * Add a product
   */
  addProduct(variantId, isAddon) {
    const products = isAddon ? this.data.products.addons : this.data.products.soaps;
    const product = products.find(p => p.variantId.toString() === variantId.toString());
    if (!product) return;

    const items = isAddon ? this.state.addons : this.state.soaps;
    
    // For soaps, check if we've hit the limit
    if (!isAddon) {
      const currentCount = this.getSelectedSoapCount();
      if (currentCount >= this.state.quantity) {
        this.showNotification('You\'ve selected all your bars! Remove one to add a different soap.');
        return;
      }
    }

    if (items[variantId]) {
      items[variantId].quantity++;
    } else {
      items[variantId] = { product, quantity: 1 };
    }

    this.updateProductCard(variantId, isAddon);
    this.updateUI();
  }

  /**
   * Update product quantity
   */
  updateProductQuantity(variantId, delta, isAddon) {
    const items = isAddon ? this.state.addons : this.state.soaps;
    
    if (!items[variantId]) return;

    // For soaps, check limit on increase
    if (!isAddon && delta > 0) {
      const currentCount = this.getSelectedSoapCount();
      if (currentCount >= this.state.quantity) {
        this.showNotification('You\'ve selected all your bars!');
        return;
      }
    }

    items[variantId].quantity += delta;

    if (items[variantId].quantity <= 0) {
      delete items[variantId];
    }

    this.updateProductCard(variantId, isAddon);
    this.updateUI();
  }

  /**
   * Update a product card's display
   */
  updateProductCard(variantId, isAddon) {
    const grid = isAddon ? this.elements.addonsGrid : this.elements.soapsGrid;
    const card = grid?.querySelector(`[data-variant-id="${variantId}"]`);
    
    // Update card in full grid if found
    if (card) {
      const items = isAddon ? this.state.addons : this.state.soaps;
      const item = items[variantId];

      const addBtn = card.querySelector('[data-action="add"]');
      const qtyControls = card.querySelector('.membership-product__qty');
      const qtyValue = card.querySelector('.membership-product__qty-value');

    if (item && item.quantity > 0) {
      card.classList.add('selected');
      if (addBtn) addBtn.style.display = 'none';
      if (qtyControls) qtyControls.style.display = 'flex';
      if (qtyValue) qtyValue.textContent = item.quantity;
    } else {
      card.classList.remove('selected');
      if (addBtn) addBtn.style.display = 'block';
      if (qtyControls) qtyControls.style.display = 'none';
      if (qtyValue) qtyValue.textContent = '0';
    }
    } // end if(card)
    
    // If addon changed from full grid, also re-render routine curated cards to stay in sync
    if (isAddon && this.elements.routineSection?.style.display !== 'none') {
      this.renderRoutineCards();
    }
  }

  /**
   * Select a plan
   */
  selectPlan(planId) {
    this.state.plan = planId;

    // Remove the no-plan state - products are now selectable
    if (this.elements.builderContainer) {
      this.elements.builderContainer.classList.remove('membership-builder--no-plan');
    }

    // Update card selection
    this.elements.planCards.forEach(card => {
      card.classList.toggle('selected', card.dataset.plan === planId);
    });

    this.updateUI();
    this.updateSteps();
  }

  /**
   * Select a quantity
   */
  selectQuantity(quantity) {
    const oldQuantity = this.state.quantity;
    this.state.quantity = quantity;

    // Remove the no-quantity state - prices are now visible
    if (this.elements.builderContainer) {
      this.elements.builderContainer.classList.remove('membership-builder--no-quantity');
    }

    // Update card selection
    this.elements.quantityCards.forEach(card => {
      card.classList.toggle('selected', parseInt(card.dataset.quantity) === quantity);
    });

    // If reducing quantity, we may need to trim selections
    if (oldQuantity && quantity < oldQuantity) {
      this.trimSoapSelections();
    }

    this.updateUI();
    this.updateSteps();
  }

  /**
   * Trim soap selections if quantity decreased
   */
  trimSoapSelections() {
    let currentCount = this.getSelectedSoapCount();
    
    while (currentCount > this.state.quantity) {
      // Remove from the last item added
      const variantIds = Object.keys(this.state.soaps);
      if (variantIds.length === 0) break;

      const lastId = variantIds[variantIds.length - 1];
      this.state.soaps[lastId].quantity--;
      
      if (this.state.soaps[lastId].quantity <= 0) {
        delete this.state.soaps[lastId];
      }

      this.updateProductCard(lastId, false);
      currentCount = this.getSelectedSoapCount();
    }
  }

  /**
   * Get total count of selected soaps
   */
  getSelectedSoapCount() {
    return Object.values(this.state.soaps).reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Filter add-ons
   */
  filterAddons(filter) {
    this.state.currentFilter = filter;

    // Update filter buttons
    this.elements.filters.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.filter === filter);
    });

    // Show/hide products
    const cards = this.elements.addonsGrid?.querySelectorAll('.membership-product');
    cards?.forEach(card => {
      const category = card.dataset.category;
      const show = filter === 'all' || category === filter;
      card.style.display = show ? '' : 'none';
    });
  }

  /**
   * Update all UI elements
   */
  updateUI() {
    this.updateCounters();
    this.updatePrices();
    this.updateSummary();
    this.updateCheckoutButton();
    this.updateStickyBar();
    this.checkRoutineReveal();
    this.saveState();
  }

  /**
   * Reveal/hide Complete Your Routine section when soaps are filled
   */
  checkRoutineReveal() {
    const totalBars = this.getSelectedSoapCount();
    const routineSection = this.elements.routineSection;
    if (!routineSection) return;
    
    const wasFilled = routineSection.style.display !== 'none';
    const isFilled = totalBars === this.state.quantity && this.state.quantity > 0;
    
    if (isFilled && !wasFilled) {
      // Just became filled — compress soap grid and reveal routine
      if (this.elements.soapsGrid) {
        this.elements.soapsGrid.classList.add('membership-products__grid--compact');
      }
      this.renderRoutineCards();
      routineSection.style.display = 'block';
    } else if (!isFilled && wasFilled) {
      // Soaps changed — expand grid back and hide routine
      if (this.elements.soapsGrid) {
        this.elements.soapsGrid.classList.remove('membership-products__grid--compact');
      }
      routineSection.style.display = 'none';
      
      // Also collapse full grid if it was open
      if (this.elements.fullAddonsGrid) {
        this.elements.fullAddonsGrid.style.display = 'none';
      }
      if (this.elements.browseAllBtn) {
        this.elements.browseAllBtn.textContent = 'Browse all add-ons →';
      }
    }
  }

  /**
   * Render curated routine cards based on scent matching
   */
  renderRoutineCards() {
    if (!this.elements.routineGrid) return;
    
    try {
      const addons = this.data.products.addons || [];
      if (addons.length === 0) return;
      
      // Get selected soap titles for scent matching
      const selectedSoaps = Object.values(this.state.soaps).map(s => s.product.title);
      const scentProfiles = this.data.scentProfiles || {};
      
      // Build scent keyword set from selected soaps
      const selectedScents = new Set();
      selectedSoaps.forEach(title => {
        const scent = scentProfiles[title];
        if (scent && scent.toLowerCase() !== 'unscented') {
          scent.split(/[,&]/).forEach(word => {
            const trimmed = word.trim().toLowerCase();
            if (trimmed) selectedScents.add(trimmed);
          });
        }
      });
      
      // Helper: score a product by scent overlap
      const scentScore = (product) => {
        const ps = scentProfiles[product.title] || '';
        if (!ps) return { score: 0, reason: '' };
        const keywords = ps.toLowerCase().split(/[,&]/).map(w => w.trim());
        const overlap = keywords.filter(k => selectedScents.has(k));
        if (overlap.length === 0) return { score: 0, reason: '' };
        const matchSoap = selectedSoaps.find(s => {
          const sp = (scentProfiles[s] || '').toLowerCase();
          return overlap.some(k => sp.includes(k));
        });
        return { 
          score: overlap.length, 
          reason: matchSoap ? `Same scent family as your ${matchSoap}` : 'Matches your scent profile' 
        };
      };
      
      // === SLOT 1: Scent-matched beard product (beard-oil or beard-butter) ===
      const beardProducts = addons.filter(p => p.category === 'beard-oil' || p.category === 'beard-butter');
      let slot1 = null;
      let slot1Reason = '';
      
      const beardScored = beardProducts.map(p => ({ product: p, ...scentScore(p) }))
        .sort((a, b) => b.score - a.score);
      
      if (beardScored.length > 0) {
        slot1 = beardScored[0].product;
        slot1Reason = beardScored[0].reason || 'Pairs perfectly with your soap picks.';
      }
      
      // === SLOT 2: Face product ===
      const faceProducts = addons.filter(p => p.category === 'face-oil');
      let slot2 = faceProducts[0] || null;
      const slot2Reason = 'Pairs with any soap — hydrates after every wash.';
      
      // === SLOT 3: Longevity Pack ===
      let slot3 = addons.find(p => p.handle === 'longevity-pack') || null;
      const slot3Reason = 'Get more out of every bar — extends the life of your soaps.';
      
      console.log('Routine slots:', {
        slot1: slot1?.title || 'none',
        slot2: slot2?.title || 'none',
        slot3: slot3?.title || 'none',
        addonCount: addons.length,
        addonHandles: addons.map(p => p.handle)
      });
      
      // Build curated array from slots
      const curated = [
        slot1 ? { product: slot1, badge: 'match', badgeText: 'Scent match', why: slot1Reason } : null,
        slot2 ? { product: slot2, badge: 'popular', badgeText: 'Essentials', why: slot2Reason } : null,
        slot3 ? { product: slot3, badge: 'popular', badgeText: 'Extend your bars', why: slot3Reason } : null,
      ].filter(Boolean);
      
      if (curated.length === 0) return;
      
      const categoryLabels = {
        'beard-oil': 'Beard Oil',
        'face-oil': 'Face Oil',
        'beard-butter': 'Beard Butter',
        'lip-balm': 'Lip Balm',
        'accessories': 'Accessory'
      };
      
      this.elements.routineGrid.innerHTML = curated.map(item => {
        const p = item.product;
        const isAdded = !!this.state.addons[p.variantId];
        const qty = isAdded ? this.state.addons[p.variantId].quantity : 0;
        
        return `
          <div class="routine-card" data-routine-variant="${p.variantId}">
            <span class="routine-card__badge routine-card__badge--${item.badge}">${item.badgeText}</span>
            <div class="routine-card__image">
              ${p.image 
                ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy">`
                : ''
              }
            </div>
            <div class="routine-card__body">
              <div class="routine-card__category">${categoryLabels[p.category] || 'Add-On'}</div>
              <div class="routine-card__title">${this.escapeHtml(p.title)}</div>
              <div class="routine-card__why">${item.why}</div>
              <div class="routine-card__footer">
                <span class="routine-card__price">${this.formatMoney(p.price)}</span>
                ${isAdded ? `
                  <div class="routine-card__qty">
                    <button class="routine-card__qty-btn" type="button" data-routine-action="decrease">−</button>
                    <span class="routine-card__qty-value">${qty}</span>
                    <button class="routine-card__qty-btn" type="button" data-routine-action="increase">+</button>
                  </div>
                ` : `
                  <button class="routine-card__add" type="button" data-routine-action="add">+ Add</button>
                `}
              </div>
            </div>
          </div>
        `;
      }).join('');
    } catch (err) {
      console.error('MembershipBuilder: renderRoutineCards error:', err);
    }
  }

  /**
   * Handle clicks on routine curated cards
   */
  handleRoutineCardClick(e) {
    const action = e.target.dataset?.routineAction;
    if (!action) return;
    
    const card = e.target.closest('.routine-card');
    if (!card) return;
    
    const variantId = card.dataset.routineVariant;
    
    if (action === 'add') {
      this.addProduct(variantId, true);
    } else if (action === 'increase') {
      this.updateProductQuantity(variantId, 1, true);
    } else if (action === 'decrease') {
      this.updateProductQuantity(variantId, -1, true);
    }
    
    this.renderRoutineCards();
  }

  /**
   * Update counter displays
   */
  updateCounters() {
    const needed = this.state.quantity || 0;
    const selected = this.getSelectedSoapCount();

    this.elements.barsNeeded.forEach(el => el.textContent = needed || '–');
    this.elements.selectedCount.forEach(el => el.textContent = selected);
  }

  /**
   * Update price displays
   */
  updatePrices() {
    // Don't update if no quantity selected
    if (!this.state.quantity) return;
    
    const retailPerBar = this.data.settings.retailPricePerBar || 10;
    const memberPerBar = this.data.settings.memberPricePerBar || 9;
    const shippingPerShipment = this.data.settings.shippingPerShipment || 5;
    const perShipment = this.state.quantity * memberPerBar;
    const savingsPerShipment = this.state.quantity * (retailPerBar - memberPerBar);
    
    // Delivery labels
    const deliveryLabels = {
      'quarterly': '1 delivery',
      '6month': '2 deliveries',
      'annual': '4 deliveries'
    };
    
    // Update each plan card
    ['annual', '6month', 'quarterly'].forEach(planId => {
      const planData = this.data.plans[planId];
      const deliveries = planData.deliveries;
      const totalBilled = perShipment * deliveries;
      const totalBarSavings = savingsPerShipment * deliveries;
      
      // Update price amount
      const priceEl = this.elements.planPrices[planId];
      if (priceEl) {
        priceEl.textContent = this.formatMoney(totalBilled);
      }
      
      // Update note
      const noteEl = this.elements.planNotes?.[planId];
      if (noteEl) {
        if (planId === 'quarterly') {
          noteEl.textContent = `${this.formatMoney(perShipment)} per shipment`;
        } else {
          noteEl.textContent = `${deliveryLabels[planId]} · ${this.formatMoney(perShipment)} per shipment`;
        }
      }
      
      // Update savings — just the total, no itemized breakdown
      const savingsEl = document.querySelector(`[data-plan-total-savings="${planId}"]`);
      if (savingsEl) {
        const totalBarSavings = savingsPerShipment * deliveries;
        const oilCredits = planData.oilCreditsTotal || 0;
        const shippingSavings = planData.freeShipping 
          ? (shippingPerShipment * deliveries) 
          : (planData.shippingSavings || 0);
        const customValue = planData.customFormulationValue || 0;
        const grandTotal = totalBarSavings + oilCredits + shippingSavings + customValue;
        
        savingsEl.textContent = this.formatMoney(grandTotal);
      }
    });
  }

  /**
   * Update summary sidebar
   */
  updateSummary() {
    const plan = this.data.plans[this.state.plan];
    
    // Plan name
    if (this.elements.summaryPlan) {
      this.elements.summaryPlan.textContent = plan.name;
    }
    
    // Bars per delivery
    if (this.elements.summaryQuantity) {
      this.elements.summaryQuantity.textContent = this.state.quantity 
        ? `${this.state.quantity} per delivery` 
        : 'Select box size';
    }

    // Oil Credits - only show for 6-month and annual
    if (this.elements.summaryOilCreditsSection) {
      if (plan.oilCreditsPerQuarter > 0) {
        this.elements.summaryOilCreditsSection.style.display = '';
        if (this.elements.summaryOilCredits) {
          this.elements.summaryOilCredits.textContent = `$${plan.oilCreditsPerQuarter}/quarter · ${this.formatMoney(plan.oilCreditsTotal)} total`;
        }
      } else {
        this.elements.summaryOilCreditsSection.style.display = 'none';
      }
    }

    // Shipping display
    if (this.elements.summaryShipping) {
      if (plan.freeShipping) {
        this.elements.summaryShipping.textContent = 'Free on all orders';
      } else if (plan.shippingSavings > 0) {
        this.elements.summaryShipping.textContent = 'Free on this order';
      } else {
        this.elements.summaryShipping.textContent = 'Standard';
      }
    }

    // Selected Soaps
    if (this.elements.summarySoaps) {
      const soapItems = Object.values(this.state.soaps);
      if (soapItems.length > 0) {
        this.elements.summarySoaps.innerHTML = soapItems
          .map(item => `<div class="membership-summary__item">${this.escapeHtml(item.product.title)} <span>×${item.quantity}</span></div>`)
          .join('');
      } else {
        this.elements.summarySoaps.innerHTML = '<p class="membership-summary__empty">No soaps selected yet</p>';
      }
    }

    // Add-ons
    const addonItems = Object.values(this.state.addons);
    if (this.elements.summaryAddonsSection) {
      this.elements.summaryAddonsSection.style.display = addonItems.length > 0 ? '' : 'none';
    }
    if (this.elements.summaryAddons && addonItems.length > 0) {
      this.elements.summaryAddons.innerHTML = addonItems
        .map(item => `<div class="membership-summary__item">${this.escapeHtml(item.product.title)} <span>×${item.quantity}</span></div>`)
        .join('');
    }

    // Totals
    const { total, quarterlyTotal } = this.calculateTotals();
    
    if (this.elements.totalAmount) {
      this.elements.totalAmount.textContent = this.formatMoney(total);
    }
    if (this.elements.quarterlyAmount) {
      this.elements.quarterlyAmount.textContent = this.formatMoney(quarterlyTotal);
    }

    // Update total label based on plan billing model
    if (this.elements.totalLabel) {
      const billingLabels = {
        'annual': 'One-Time Total (4 shipments)',
        '6month': 'One-Time Total (2 shipments)',
        'quarterly': 'Recurring Per Shipment'
      };
      this.elements.totalLabel.textContent = billingLabels[this.state.plan] || 'Today';
    }
  }

  /**
   * Calculate totals
   */
  calculateTotals() {
    // Handle no quantity selected
    if (!this.state.quantity) {
      return { total: 0, quarterlyTotal: 0 };
    }
    
    const memberPerBar = this.data.settings.memberPricePerBar || 9;
    const perShipment = this.state.quantity * memberPerBar;
    
    const planData = this.data.plans[this.state.plan];
    const deliveries = planData.deliveries;
    
    const subscriptionTotal = perShipment * deliveries;
    
    // Add-ons total (one-time, no multiplier)
    const addonsTotal = Object.values(this.state.addons)
      .reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

    return {
      total: subscriptionTotal + addonsTotal,
      quarterlyTotal: perShipment
    };
  }

  /**
   * Update checkout button state
   */
  updateCheckoutButton() {
    const selectedCount = this.getSelectedSoapCount();
    const quantity = this.state.quantity || 0;
    const isComplete = quantity > 0 && selectedCount === quantity;
    const isOverLimit = quantity > 0 && selectedCount > quantity;

    let buttonText;
    let stickyText;

    if (!quantity) {
      buttonText = 'Select Box Size First';
      stickyText = 'Select Box Size';
    } else if (isComplete) {
      buttonText = this.state.plan === 'quarterly' 
        ? 'Start Recurring Subscription' 
        : 'Complete Prepaid Order';
      stickyText = this.state.plan === 'quarterly'
        ? 'Start Subscription'
        : 'Complete Order';
    } else if (isOverLimit) {
      const excess = selectedCount - quantity;
      buttonText = `Remove ${excess} Bar${excess > 1 ? 's' : ''} to Continue`;
      stickyText = buttonText;
    } else {
      const remaining = quantity - selectedCount;
      buttonText = `Select ${remaining} More Soap${remaining > 1 ? 's' : ''}`;
      stickyText = buttonText;
    }

    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = !isComplete;
      this.elements.checkoutBtn.textContent = buttonText;
    }
    if (this.elements.stickyCheckout) {
      this.elements.stickyCheckout.disabled = !isComplete;
      this.elements.stickyCheckout.textContent = stickyText;
    }
  }

  /**
   * Update step indicators
   */
  updateSteps() {
    const selectedCount = this.getSelectedSoapCount();
    const quantity = this.state.quantity || 0;
    const soapsFilled = quantity > 0 && selectedCount === quantity;

    this.elements.steps.forEach(step => {
      const stepNum = parseInt(step.dataset.step);
      let isActive = false;

      switch (stepNum) {
        case 1: isActive = true; break;
        case 2: isActive = this.state.quantity !== null; break;
        case 3: isActive = this.state.quantity !== null && !soapsFilled; break;
        case 4: isActive = soapsFilled; break;
      }

      step.classList.toggle('active', isActive);
    });
  }

  /**
   * Initialize sticky bar
   */
  initStickyBar() {
    if (!this.elements.stickyBar) return;

    const checkVisibility = () => {
      const shouldShow = window.innerWidth <= 1024 && window.scrollY > 500;
      this.elements.stickyBar.classList.toggle('visible', shouldShow);
    };

    window.addEventListener('scroll', checkVisibility);
    checkVisibility();
    
    // Summary toggle
    if (this.elements.stickySummaryToggle) {
      this.elements.stickySummaryToggle.addEventListener('click', () => {
        const body = this.elements.stickySummaryBody;
        const chevron = this.elements.stickyChevron;
        if (!body) return;
        
        const isOpen = body.style.display !== 'none';
        body.style.display = isOpen ? 'none' : 'block';
        if (chevron) chevron.classList.toggle('expanded', !isOpen);
      });
    }
  }

  /**
   * Update sticky bar
   */
  updateStickyBar() {
    if (!this.elements.stickyBar) return;

    const selectedCount = this.getSelectedSoapCount();
    const { total } = this.calculateTotals();
    const quantity = this.state.quantity || 0;
    const isComplete = quantity > 0 && selectedCount === quantity;

    if (this.elements.stickyCount) {
      this.elements.stickyCount.textContent = selectedCount;
    }
    if (this.elements.stickyTotal) {
      this.elements.stickyTotal.textContent = quantity || '–';
    }
    if (this.elements.stickyPrice) {
      this.elements.stickyPrice.textContent = this.formatMoney(total);
    }
    if (this.elements.stickyCheckout) {
      this.elements.stickyCheckout.disabled = !isComplete;
      this.elements.stickyCheckout.textContent = isComplete ? 'Checkout' : 'Select Soaps';
    }
    
    // Populate expandable summary
    const hasSelections = this.state.plan || this.state.quantity || selectedCount > 0;
    if (this.elements.stickySummary) {
      this.elements.stickySummary.style.display = hasSelections ? 'block' : 'none';
    }
    
    // Plan name
    if (this.elements.stickyPlanName) {
      const planNames = { annual: 'Annual Executive', '6month': '6-Month Executive', quarterly: 'Quarterly' };
      this.elements.stickyPlanName.textContent = planNames[this.state.plan] || '–';
    }
    
    // Quantity
    if (this.elements.stickyQtyName) {
      this.elements.stickyQtyName.textContent = quantity ? `${quantity} bars / quarter` : '–';
    }
    
    // Soaps list
    if (this.elements.stickySoapsList) {
      const soapEntries = Object.values(this.state.soaps);
      if (soapEntries.length > 0) {
        this.elements.stickySoapsList.innerHTML = soapEntries
          .map(s => `<div>${s.product.title} × ${s.quantity}</div>`)
          .join('');
      } else {
        this.elements.stickySoapsList.textContent = '–';
      }
    }
    
    // Add-ons list
    const addonEntries = Object.values(this.state.addons);
    if (this.elements.stickyAddonsRow) {
      this.elements.stickyAddonsRow.style.display = addonEntries.length > 0 ? '' : 'none';
    }
    if (this.elements.stickyAddonsList && addonEntries.length > 0) {
      this.elements.stickyAddonsList.innerHTML = addonEntries
        .map(a => `<div>${a.product.title} × ${a.quantity}</div>`)
        .join('');
    }
    
    // Total
    if (this.elements.stickySummaryTotal) {
      this.elements.stickySummaryTotal.textContent = this.formatMoney(total);
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Re-check sticky bar visibility
    const shouldShow = window.innerWidth <= 1024 && window.scrollY > 500;
    this.elements.stickyBar?.classList.toggle('visible', shouldShow);
  }

  /**
   * Checkout - matches old system's Recharge integration
   */
  async checkout() {
    const selectedCount = this.getSelectedSoapCount();
    if (selectedCount !== this.state.quantity) {
      this.showNotification(`Please select ${this.state.quantity} soaps before checkout.`);
      return;
    }

    // Disable buttons during submission
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = true;
      this.elements.checkoutBtn.textContent = 'Processing...';
    }
    if (this.elements.stickyCheckout) {
      this.elements.stickyCheckout.disabled = true;
      this.elements.stickyCheckout.textContent = 'Processing...';
    }

    try {
      const cartItems = [];
      
      // Plan data from data layer
      const planData = this.data.plans[this.state.plan];
      const deliveryMultiplier = planData.deliveries;
      const sellingPlans = this.data.sellingPlans || {};
      const sellingPlansFallback = this.data.sellingPlansFallback || {};
      const membershipConfig = this.data.membershipConfig?.[this.state.plan] || {};
      
      // Helper: resolve selling plan ID for a product handle + plan tier
      const resolveSellingPlan = (handle, planTier) => {
        // Try dynamic data first
        const dynamic = sellingPlans[handle];
        if (dynamic && dynamic.plans) {
          const nameLower = planTier === 'quarterly' ? ['quarterly', '3 month', 'every 3']
            : planTier === '6month' ? ['6 month', '6-month', 'six month']
            : ['annual', '12 month', '1 year'];
          
          const match = dynamic.plans.find(p => {
            const pName = p.name.toLowerCase();
            return nameLower.some(keyword => pName.includes(keyword));
          });
          
          if (match) {
            console.log(`Selling plan resolved dynamically: ${handle} → ${planTier} = ${match.id} (${match.name})`);
            return match.id;
          }
        }
        
        // Fallback to hardcoded IDs
        const fb = sellingPlansFallback[handle];
        if (fb && fb[planTier]) {
          console.log(`Selling plan resolved from fallback: ${handle} → ${planTier} = ${fb[planTier]}`);
          return fb[planTier];
        }
        
        console.warn(`No selling plan found for ${handle} / ${planTier}`);
        return null;
      };
      
      // Calculate membership dates
      const startDate = new Date();
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (membershipConfig.durationDays || 90));
      const graceEndDate = new Date(endDate);
      graceEndDate.setDate(graceEndDate.getDate() + (membershipConfig.gracePeriodDays || 0));
      
      const formatDate = (d) => d.toISOString().split('T')[0];
      
      // Build subscription items with Recharge selling_plan
      Object.values(this.state.soaps).forEach(item => {
        const handle = item.product.handle;
        const sellingPlanId = resolveSellingPlan(handle, this.state.plan);
        
        const properties = {
          'Price': '$9/bar · 10% member discount',
          'Quantity': `${item.quantity} per delivery (${deliveryMultiplier} ${deliveryMultiplier === 1 ? 'delivery' : 'deliveries'})`
        };
        
        const cartItem = {
          id: parseInt(item.product.variantId),
          quantity: sellingPlanId ? item.quantity : item.quantity * deliveryMultiplier,
          properties: properties
        };
        
        // Attach selling plan for Recharge subscription + discount
        if (sellingPlanId) {
          cartItem.selling_plan = sellingPlanId;
        }
        
        cartItems.push(cartItem);
      });
      
      // Build addon items (one-time, no subscription)
      Object.values(this.state.addons).forEach(item => {
        cartItems.push({
          id: parseInt(item.product.variantId),
          quantity: item.quantity,
          properties: {
            'Type': 'One-Time Add-On'
          }
        });
      });

      console.log('Adding items to cart:', cartItems);

      // Clear cart first, then add new items
      try {
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (clearErr) {
        console.warn('Cart clear failed, continuing:', clearErr);
      }

      // Add items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: cartItems })
      });

      // Check if cart add failed (e.g. invalid selling_plan)
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        console.error('Cart add failed:', response.status, errorBody);
        
        // Retry without selling_plan if it failed
        console.log('Retrying without selling_plan...');
        const fallbackItems = cartItems.map(item => {
          const { selling_plan, ...rest } = item;
          return rest;
        });
        
        const retryResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: fallbackItems })
        });
        
        if (!retryResponse.ok) {
          throw new Error('Cart add failed on retry');
        }
        
        const retryData = await retryResponse.json();
        console.log('Cart retry response (no selling_plan):', retryData);
      } else {
        const data = await response.json();
        console.log('Cart API response:', data);
      }
      
      // Set cart attributes for membership tagging (non-blocking)
      try {
        const tags = [membershipConfig.tag];
        if (membershipConfig.oilCreditTag) tags.push(membershipConfig.oilCreditTag);
        if (membershipConfig.freeShippingTag) tags.push(membershipConfig.freeShippingTag);
        if (membershipConfig.customFormulationTag) tags.push(membershipConfig.customFormulationTag);
        
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Membership Plan': this.state.plan,
              'Membership Tier': membershipConfig.tag || '',
              'Membership Tags': tags.filter(Boolean).join(', '),
              'Membership Start': formatDate(startDate),
              'Membership End': formatDate(endDate),
              'Grace Period End': formatDate(graceEndDate),
              'Oil Credits Per Quarter': String(planData.oilCreditsPerQuarter || 0),
              'Oil Credits Total': String(planData.oilCreditsTotal || 0),
              'Bars Per Shipment': String(this.state.quantity),
              'Total Deliveries': String(deliveryMultiplier)
            }
          })
        });
      } catch (attrErr) {
        console.warn('Cart attributes failed, continuing to checkout:', attrErr);
      }

      // Push membership properties to Klaviyo profile
      try {
        var _learnq = window._learnq || [];
        _learnq.push(['identify', {
          'Membership Plan Tag': membershipConfig.tag || '',
          'Membership Start': formatDate(startDate),
          'Membership End': formatDate(endDate),
          'Grace Period End': formatDate(graceEndDate),
          'Oil Credits Per Quarter': planData.oilCreditsPerQuarter || 0,
          'Bars Per Shipment': this.state.quantity,
          'Total Deliveries': deliveryMultiplier
        }]);

        _learnq.push(['track', 'Membership Checkout Started', {
          'Membership Plan Tag': membershipConfig.tag || '',
          'Membership Start': formatDate(startDate),
          'Membership End': formatDate(endDate),
          'Grace Period End': formatDate(graceEndDate),
          'Oil Credits Per Quarter': planData.oilCreditsPerQuarter || 0,
          'Bars Per Shipment': this.state.quantity,
          'Total Deliveries': deliveryMultiplier
        }]);
      } catch (klaviyoErr) {
        console.warn('Klaviyo tracking failed, continuing to checkout:', klaviyoErr);
      }

      // Redirect to checkout
      setTimeout(() => {
        window.location.href = '/checkout';
      }, 300);

    } catch (error) {
      console.error('Checkout error:', error);
      this.showNotification('Something went wrong. Please try again.');
      this.resetCheckoutButtons();
    }
  }

  /**
   * Reset checkout buttons after error
   */
  resetCheckoutButtons() {
    const buttonText = this.state.plan === 'quarterly' 
      ? 'Try Again - Start Subscription' 
      : 'Try Again - Complete Order';
    
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = false;
      this.elements.checkoutBtn.textContent = buttonText;
    }
    if (this.elements.stickyCheckout) {
      this.elements.stickyCheckout.disabled = false;
      this.elements.stickyCheckout.textContent = 'Try Again';
    }
  }

  /**
   * Show notification toast - matches old system styling
   */
  showNotification(message, type = 'info') {
    const messageEl = document.createElement('div');
    messageEl.className = `membership-message membership-message--${type}`;
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#ff4444' : type === 'success' ? '#4a7c59' : '#333'};
      color: white;
      padding: 15px 20px;
      border-radius: 4px;
      z-index: 9999;
      max-width: 300px;
      animation: slideIn 0.3s ease;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      font-family: var(--font-grotesk, sans-serif);
      font-size: 14px;
    `;
    
    document.body.appendChild(messageEl);
    
    setTimeout(() => {
      messageEl.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => messageEl.remove(), 300);
    }, 3000);
  }

  /**
   * Format money
   */
  formatMoney(amount) {
    return `$${amount.toFixed(2).replace(/\.00$/, '')}`;
  }

  /**
   * Escape HTML
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Initialize on DOM ready
function initMembershipBuilder() {
  // Check if data exists
  const dataEl = document.getElementById('membership-data');
  if (!dataEl) {
    console.log('MembershipBuilder: No data element found, skipping init');
    return;
  }

  window.membershipBuilder = new MembershipBuilder();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMembershipBuilder);
} else {
  initMembershipBuilder();
}

// Theme Editor support
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', initMembershipBuilder);
}

// Add animation styles for notifications
const notificationStyles = document.createElement('style');
notificationStyles.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(notificationStyles);